// src/features/fit/components/FitPage.tsx
//
// "Product C minimal tool (four dimensions, before/after)" (w7.u5, D16/D25:
// solution fitness, internal to Roommatik only) - route /fit. GET
// /api/v2/ui/fit (w7.u1) returns the fit.* ids over the de-identified
// projection for one property/period and an optional cut on one of the
// v2.0 dimensions (app_version / flow_config_ref / language - the fourth
// dimension, per-step attempts with method, is inside the metrics
// themselves, e.g. fit.method_fallback - not a fifth cut parameter the
// endpoint takes). "Minimal": free-text cut fields, not a values picker -
// no endpoint enumerates the known app versions or configuration hashes for
// a property, so a dropdown would have to guess at values; stated here
// rather than built as a silent no-op.
//
// "Before/after view per deployment" is built as two independent scenarios
// (A always fetched, B only when the toggle is on), each with its own
// period and cuts, fetched and rendered side by side - not a computed diff:
// the read API has no pairwise-comparison endpoint, and inventing one is
// out of this unit's size.
import React from 'react';
import { Box, Checkbox, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import { useDataProvider, useTranslate } from 'react-admin';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { WidgetCard } from '@/components/common/WidgetCard';
import { PeriodPicker, defaultPeriod, type PeriodValue } from '@/components/common/PeriodPicker';
import { useV2PropertySelection } from '@/hooks/useV2PropertySelection';
import { useAnalyticsFetch } from '@/hooks/useAnalyticsFetch';
import { PropertySelector } from '@/features/insight-v2-shared/components/PropertySelector';
import { PropertyState } from '@/features/insight-v2-shared/components/PropertyState';
import { MetricPanelSection } from '@/features/insight-v2-shared/components/MetricPanelSection';
import type { MetricResultDto } from '@/types/analyticsV2';

interface CutValue {
  appVersion: string;
  flowConfigRef: string;
  language: string;
}

const emptyCut: CutValue = { appVersion: '', flowConfigRef: '', language: '' };

const CutFields: React.FC<{ value: CutValue; onChange: (next: CutValue) => void }> = ({ value, onChange }) => {
  const translate = useTranslate();
  return (
    <Grid container spacing={1.5}>
      <Grid item xs={12} sm={4}>
        <TextField
          size="small"
          fullWidth
          label={translate('resources.fit.cuts.appVersion')}
          value={value.appVersion}
          onChange={(e) => onChange({ ...value, appVersion: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          size="small"
          fullWidth
          label={translate('resources.fit.cuts.flowConfigRef')}
          value={value.flowConfigRef}
          onChange={(e) => onChange({ ...value, flowConfigRef: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          size="small"
          fullWidth
          label={translate('resources.fit.cuts.language')}
          value={value.language}
          onChange={(e) => onChange({ ...value, language: e.target.value })}
        />
      </Grid>
    </Grid>
  );
};

const Scenario: React.FC<{
  titleKey: string;
  propertyId: string;
  period: PeriodValue;
  onPeriodChange: (p: PeriodValue) => void;
  cut: CutValue;
  onCutChange: (c: CutValue) => void;
}> = ({ titleKey, propertyId, period, onPeriodChange, cut, onCutChange }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();

  const { data, loading, error, refresh } = useAnalyticsFetch<MetricResultDto[]>(
    () =>
      (dataProvider as any).getAnalyticsFit({
        property: propertyId,
        from: period.from,
        to: period.to,
        appVersion: cut.appVersion || undefined,
        flowConfigRef: cut.flowConfigRef || undefined,
        language: cut.language || undefined,
      }),
    [dataProvider, propertyId, period.from, period.to, cut.appVersion, cut.flowConfigRef, cut.language]
  );

  return (
    <WidgetCard title={translate(titleKey)} sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
        <PeriodPicker value={period} onChange={onPeriodChange} showCompare={false} />
        <CutFields value={cut} onChange={onCutChange} />
      </Box>
      {error && <FetchErrorBanner onRetry={refresh} />}
      <MetricPanelSection title={translate('resources.fit.list.title')} metrics={data ?? []} loading={loading} />
    </WidgetCard>
  );
};

export const FitPage: React.FC = () => {
  const translate = useTranslate();
  const property = useV2PropertySelection('fit');

  const { propertyId } = property;
  const [periodA, setPeriodA] = React.useState<PeriodValue>(() => defaultPeriod());
  const [cutA, setCutA] = React.useState<CutValue>(emptyCut);
  const [compare, setCompare] = React.useState(false);
  const [periodB, setPeriodB] = React.useState<PeriodValue>(() => defaultPeriod());
  const [cutB, setCutB] = React.useState<CutValue>(emptyCut);

  return (
    <Box>
      <SectionHeader manualScreen="fit"
        left={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <PropertySelector selection={property} />
            <FormControlLabel
              control={<Checkbox checked={compare} onChange={(e) => setCompare(e.target.checked)} />}
              label={translate('resources.fit.compare.toggle')}
            />
          </Box>
        }
        subtitle={translate('resources.fit.subtitle')}
        helpConfig={{
          titleKey: 'resources.fit.help.title',
          descriptionKey: 'resources.fit.help.description',
          featureKeys: ['resources.fit.help.features.cuts', 'resources.fit.help.features.compare'],
          tipKeys: ['resources.fit.help.tips.internal'],
        }}
      />

      {!propertyId ? (
        <PropertyState selection={property} />
      ) : (
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={compare ? 6 : 12}>
            <Scenario
              titleKey={compare ? 'resources.fit.compare.before' : 'resources.fit.help.title'}
              propertyId={propertyId}
              period={periodA}
              onPeriodChange={setPeriodA}
              cut={cutA}
              onCutChange={setCutA}
            />
          </Grid>
          {compare && (
            <Grid item xs={12} md={6}>
              <Scenario
                titleKey="resources.fit.compare.after"
                propertyId={propertyId}
                period={periodB}
                onPeriodChange={setPeriodB}
                cut={cutB}
                onCutChange={setCutB}
              />
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default FitPage;
